import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Divider
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { fetchCurrencySetting } from '../utils/fetchCurrencySetting';

export default function InvoiceDialog({ open, sale, onClose, customerPending }) {
  const [currency, setCurrency] = useState('USD');
  const [receiptSettings, setReceiptSettings] = useState({
    businessName: 'YOUR STORE NAME',
    businessAddress: '123 Business St, City, ST 12345',
    businessPhone: '(123) 456-7890',
    businessEmail: '',
    receiptHeader: '',
    receiptFooter: '',
    showTaxBreakdown: true,
    showItemCodes: false,
    showCashierName: true,
    thankYouMessage: 'Thank you for your business!'
  });

  useEffect(() => {
    if (open) {
      (async () => {
        const curr = await fetchCurrencySetting();
        setCurrency(curr);
        
        // Fetch receipt settings
        try {
          const res = await fetch('/api/settings/receipt');
          if (res.ok) {
            const settings = await res.json();
            if (settings && Object.keys(settings).length > 0) {
              setReceiptSettings(prev => ({ ...prev, ...settings }));
            }
          }
        } catch (err) {
          console.error('Failed to fetch receipt settings:', err);
        }
      })();
    }
  }, [open]);

  // Calculate subtotal dynamically - must be before early return
  const subtotal = useMemo(() => {
    if (!sale?.products || sale.products.length === 0) {
      return sale?.total || 0; // Fallback to sale.total
    }
    
    const total = sale.products.reduce((sum, p) => {
      const itemTotal = (Number(p.quantity) || 0) * (Number(p.price) || 0);
      return sum + itemTotal;
    }, 0);
    
    // If calculated total is 0 but sale.total exists, use sale.total
    if (total === 0 && sale?.total) {
      return sale.total + (sale.discount || 0); // Add back any discount to get subtotal
    }
    
    return total;
  }, [sale?.products, sale?.total, sale?.discount]);

  // Calculate total discount amount
  const totalDiscount = useMemo(() => {
    let itemDiscounts = 0;
    if (sale?.products) {
      itemDiscounts = sale.products.reduce((sum, item) => {
        const originalPrice = item.product?.price || 0;
        if (item.price < originalPrice) {
          const discountPerItem = originalPrice - item.price;
          return sum + (discountPerItem * item.quantity);
        }
        return sum;
      }, 0);
    }
    const globalDiscount = sale?.discount || 0;
    return itemDiscounts + globalDiscount;
  }, [sale?.products, sale?.discount]);

  const hasGlobalDiscount = sale?.discount && sale.discount > 0;
  const hasItemDiscounts = sale?.products?.some((item) => {
    const originalPrice = item.product?.price || 0;
    return item.price < originalPrice;
  });

  // Calculate total items count
  const totalItemsCount = useMemo(() => {
    if (!sale?.products) return 0;
    return sale.products.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);
  }, [sale?.products]);

  if (!sale) return null;

  const formatCurrency = (amount) => {
    return amount.toLocaleString(undefined, { style: 'currency', currency });
  };

  const renderCustomerName = () => {
    if (sale.customer?.name) return sale.customer.name;
    if (sale.customerName) return sale.customerName;
    return 'Walk-in Customer';
  };

  return (
    <>
      <style>
        {`
          @media print {
            @page {
              margin: 0;
              size: auto;
            }
            body {
              margin: 1cm;
            }
          }
        `}
      </style>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            maxWidth: '320px'
          }
        }}
      >
      <DialogContent sx={{ p: 1.5, bgcolor: '#ffffff' }}>
        {/* Store Header */}
        <Box sx={{ textAlign: 'center', mb: 1.5, pb: 1, borderBottom: '1px dashed #ccc' }}>
          <Typography variant="body2" fontWeight="700" sx={{ mb: 0.3, fontSize: '0.9rem' }}>
            {receiptSettings.businessName}
          </Typography>
          {receiptSettings.businessAddress && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
              {receiptSettings.businessAddress}
            </Typography>
          )}
          {receiptSettings.businessPhone && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
              Tel: {receiptSettings.businessPhone}
            </Typography>
          )}
          {receiptSettings.businessEmail && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {receiptSettings.businessEmail}
            </Typography>
          )}
        </Box>

        {/* Custom Header Text */}
        {receiptSettings.receiptHeader && (
          <Box sx={{ textAlign: 'center', mb: 1, pb: 1, borderBottom: '1px dashed #eee' }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontStyle: 'italic', color: 'text.secondary' }}>
              {receiptSettings.receiptHeader}
            </Typography>
          </Box>
        )}

        {/* Transaction Info - Ultra Compact */}
        <Stack spacing={0.3} sx={{ mb: 1.5, fontSize: '0.7rem' }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Date:</Typography>
            <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.7rem' }}>
              {new Date(sale.createdAt).toLocaleDateString('en-US', { 
                month: '2-digit', 
                day: '2-digit',
                year: '2-digit'
              })} {new Date(sale.createdAt).toLocaleTimeString('en-US', { 
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              })}
            </Typography>
          </Stack>
          {receiptSettings.showCashierName && (
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Cashier:</Typography>
              <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.7rem' }}>
                {sale.cashier?.username || sale.cashier?.name || sale.user?.username || 'Staff'}
              </Typography>
            </Stack>
          )}
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Customer:</Typography>
            <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.7rem' }}>
              {renderCustomerName()}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Invoice:</Typography>
            <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.7rem' }}>
              {sale.invoiceNumber || `#${sale._id?.slice(-8).toUpperCase()}`}
            </Typography>
          </Stack>
        </Stack>

        <Divider sx={{ borderStyle: 'dashed', mb: 1 }} />

        {/* Items - Compact List */}
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          {sale.products?.map((item, idx) => {
            const originalPrice = item.product?.price || 0;
            const isDiscounted = item.price < originalPrice;
            const discountPercent = isDiscounted
              ? ((originalPrice - item.price) / originalPrice * 100).toFixed(0)
              : 0;

            return (
              <Box key={idx}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: '0.75rem', 
                        fontWeight: 600,
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {item.product?.name || 'Unknown'}
                    </Typography>
                    {receiptSettings.showItemCodes && item.product?.itemNumber && (
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                        SKU: {item.product.itemNumber}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                      {isDiscounted ? (
                        <>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: '0.65rem', 
                              color: 'text.secondary',
                              textDecoration: 'line-through'
                            }}
                          >
                            {item.quantity} × {formatCurrency(originalPrice)}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#000', fontWeight: 600 }}>
                            {item.quantity} × {formatCurrency(item.price)}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: '0.6rem',
                              color: '#1976d2',
                              bgcolor: '#e3f2fd',
                              px: 0.5,
                              py: 0.1,
                              borderRadius: 0.5,
                              fontWeight: 700
                            }}
                          >
                            -{discountPercent}%
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                          {item.quantity} × {formatCurrency(item.price)}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  <Typography 
                    variant="caption" 
                    fontWeight="700" 
                    sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                  >
                    {formatCurrency(item.quantity * item.price)}
                  </Typography>
                </Stack>
                {idx < sale.products.length - 1 && (
                  <Divider sx={{ my: 0.5, borderStyle: 'dotted' }} />
                )}
              </Box>
            );
          })}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed', my: 1 }} />

        {/* Totals - Ultra Compact */}
        <Stack spacing={0.3} sx={{ mb: 1 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Subtotal ({totalItemsCount} {totalItemsCount === 1 ? 'item' : 'items'}):</Typography>
            <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.7rem' }}>
              {formatCurrency(subtotal)}
            </Typography>
          </Stack>

          {hasGlobalDiscount && (
            <>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#1976d2' }}>Discount:</Typography>
                <Typography variant="caption" fontWeight="700" sx={{ fontSize: '0.7rem', color: '#1976d2' }}>
                  -{formatCurrency(sale.discount)}
                </Typography>
              </Stack>
            </>
          )}

          <Divider sx={{ my: 0.5 }} />

          <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5, bgcolor: '#f5f5f5', px: 1, mx: -1 }}>
            <Typography variant="body2" fontWeight="700" sx={{ fontSize: '0.85rem' }}>
              TOTAL
            </Typography>
            <Typography variant="body2" fontWeight="700" sx={{ fontSize: '0.9rem' }}>
              {formatCurrency(sale.total)}
            </Typography>
          </Stack>

          <Divider sx={{ my: 0.5 }} />

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Paid:</Typography>
            <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.7rem' }}>
              {formatCurrency(sale.paidAmount || sale.paid || sale.total)}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Change:</Typography>
            <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.7rem' }}>
              {formatCurrency(sale.change || 0)}
            </Typography>
          </Stack>

          {sale.paymentMethod && (
            <Stack direction="row" justifyContent="space-between" sx={{ pt: 0.3 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Payment:</Typography>
              <Typography variant="caption" fontWeight="700" sx={{ fontSize: '0.7rem' }}>
                {sale.paymentMethod.toUpperCase()}
              </Typography>
            </Stack>
          )}

          {totalDiscount > 0 && (
            <>
              <Divider sx={{ my: 0.5, borderStyle: 'dashed' }} />
              <Stack direction="row" justifyContent="space-between" sx={{ pt: 0.3, pb: 0.3 }}>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#2e7d32', fontWeight: 600 }}>
                  Total Saved:
                </Typography>
                <Typography variant="caption" fontWeight="700" sx={{ fontSize: '0.75rem', color: '#2e7d32' }}>
                  {formatCurrency(totalDiscount)}
                </Typography>
              </Stack>
            </>
          )}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed', my: 1 }} />

        {/* Customer Pending Bills - Show if exists */}
        {customerPending && customerPending.total > 0 && (
          <>
            <Box sx={{ 
              bgcolor: '#fff3cd', 
              border: '1px solid #ffc107', 
              borderRadius: 1, 
              p: 1, 
              mb: 1 
            }}>
              <Typography 
                variant="caption" 
                fontWeight="700" 
                sx={{ 
                  fontSize: '0.75rem', 
                  display: 'block', 
                  mb: 0.5,
                  color: '#856404'
                }}
              >
                ⚠ CUSTOMER OUTSTANDING BALANCE
              </Typography>
              <Stack spacing={0.3}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#856404' }}>
                    Previous Unpaid Bills:
                  </Typography>
                  <Typography variant="caption" fontWeight="700" sx={{ fontSize: '0.7rem', color: '#856404' }}>
                    {customerPending.count}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem', color: '#856404' }}>
                    Total Pending Amount:
                  </Typography>
                  <Typography variant="caption" fontWeight="700" sx={{ fontSize: '0.8rem', color: '#856404' }}>
                    {formatCurrency(customerPending.total)}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
            <Divider sx={{ borderStyle: 'dashed', my: 1 }} />
          </>
        )}

        {/* Footer */}
        {receiptSettings.receiptFooter && (
          <>
            <Divider sx={{ borderStyle: 'dashed', my: 1 }} />
            <Box sx={{ textAlign: 'center', py: 0.5, px: 1 }}>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                {receiptSettings.receiptFooter}
              </Typography>
            </Box>
          </>
        )}
        
        <Box sx={{ textAlign: 'center', py: 0.5 }}>
          <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem', display: 'block', mb: 0.3 }}>
            {receiptSettings.thankYouMessage}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
            Please come again
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        p: 1, 
        bgcolor: '#f5f5f5', 
        borderTop: '1px solid #ddd', 
        gap: 1,
        '@media print': {
          display: 'none'
        }
      }}>
        <Button 
          onClick={onClose} 
          size="small"
          sx={{ 
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.75rem',
            minWidth: 60
          }}
        >
          Close
        </Button>
        <Button
          onClick={() => window.print()}
          variant="contained"
          size="small"
          sx={{
            bgcolor: '#000',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.75rem',
            minWidth: 80,
            '&:hover': {
              bgcolor: '#333'
            }
          }}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
